import type { AutocompleteTextFieldProps } from '@cmg/design-system';
import { Autocomplete } from '@cmg/design-system';
import { useFormikContext } from 'formik';
import type { VFC } from 'react';
import { useMemo } from 'react';

import { FormFieldLayout } from '../../../../../design-system/body-sections/form/FormFieldLayout';
import { FormLayout } from '../../../../../design-system/body-sections/form/FormLayout';
import { AccountType } from '../../../../../types/domain/account/constants';
import { useTraitList_AccountTraitsQuery } from '../../../../global-management/trait-list/components/trait-list/graphql';
import useCertificatesAccess from '../../../../shared/hooks/useCertificatesAccess';
import type { ProductTraitsFormValues } from './ProductTraitsSection.model';

export type Props = {
  accountType: Readonly<AccountType | null>;
};

export const ProductTraitsSectionEdit: VFC<Props> = ({ accountType }) => {
  const formik = useFormikContext<ProductTraitsFormValues>();
  const { shouldShowTrait } = useCertificatesAccess();

  const traitCodesTextFieldProps: AutocompleteTextFieldProps = useMemo(
    () => ({
      name: 'traitCodes',
      label: 'Existing Traits',
      error: Boolean(formik.errors.traitCodes),
      helperText: formik.errors.traitCodes,
      placeholder: 'Assign Traits...',
    }),
    [formik.errors.traitCodes]
  );

  const { data, loading } = useTraitList_AccountTraitsQuery();

  let traitOptions;

  if (accountType === AccountType.BUY_SIDE) {
    traitOptions = data?.accountTraits.filter(
      trait =>
        trait.accountType === 'BUY_SIDE' ||
        trait.accountType === 'ALL_CUSTOMERS' ||
        trait.accountType === 'ALL'
    );
  } else if (accountType === AccountType.SELL_SIDE) {
    traitOptions = data?.accountTraits
      .filter(
        trait =>
          trait.accountType === 'SELL_SIDE' ||
          trait.accountType === 'ALL_CUSTOMERS' ||
          trait.accountType === 'ALL'
      )
      .filter(option => shouldShowTrait(option.code));
  } else {
    // System Account
    traitOptions = data?.accountTraits
      .filter(
        trait =>
          trait.accountType === 'BUY_SIDE' ||
          trait.accountType === 'SELL_SIDE' ||
          trait.accountType === 'SYSTEM' ||
          trait.accountType === 'ALL_CUSTOMERS' ||
          trait.accountType === 'ALL'
      )
      .filter(option => shouldShowTrait(option.code));
  }

  return (
    <FormLayout>
      <FormFieldLayout>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <Autocomplete
            multiple
            limitTags={2}
            options={traitOptions?.map(trait => trait.code) || []}
            getOptionLabel={optionCode =>
              traitOptions?.find(oc => oc.code === optionCode)?.name.toUpperCase() ?? ''
            }
            value={formik.values.traitCodes}
            onChange={(_e, value) => {
              void formik.setFieldValue('traitCodes', value);
            }}
            TextFieldProps={traitCodesTextFieldProps}
          />
        )}
      </FormFieldLayout>
    </FormLayout>
  );
};
